import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import useSWR from "swr";
import { format } from "date-fns";
import { ListTodoIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip } from "@/components/badge/chip";
import { RowCard } from "@/components/list/row-card";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { TagPill } from "@/components/prds/tag-pill";
import IssueTypeChip from "@/components/prds/issue-type-chip";
import OwnerCell from "@/components/prds/owner-cell";
import { STATE_PLANNING } from "@/constants/version-state";
import { engagementDetailPath } from "@/lib/paths";
import { useApp } from "@/hooks/apps";
import { mutateRequest, swrKey } from "@/lib/swr";
import { cn } from "@/lib/utils";
import { PriorityChip } from "@/components/engagements/priority-chip";
import { ScheduleVersionDialog } from "@/components/prds/schedule-version-dialog";
import BackToPoolButton from "@/components/engagements/back-to-pool-button";
import type { Prd, WishPoolFilter, WishPoolResult, WishPoolScope } from "@/types/domain";
import { DataTablePagination } from "@/components/zthd/data-table-pagination-mysql";
import { useTags } from "@/hooks/tags";
import { engagementVersion } from "@/lib/engagement";
import { DATE_SHORT } from "@/lib/date";
import { CopyShortId } from "@/components/prds/copy-short-id";
import { CompletionProgress, MyStatusIcon } from "@/components/engagements/completion-progress";
import { personalStatusOf } from "@/components/engagements/my-completion";
import { PrLinks } from "@/components/prds/pr-links";
import { ExecStageChip } from "@/components/prds/exec-stage-chip";
import { ExecMenu } from "@/components/prds/exec-menu";


// Personal rhythm lives here: my pending work on this App's engagements, plus the
// whole App as a second view. Version rhythm is the PM's — see Versions
// (WED-218: no version-state buckets on this page).
const SCOPES: { value: WishPoolScope; label: string }[] = [
  { value: "me", label: "Me" },
  { value: "all", label: "All" },
];
// All splits into two buckets by everyone's overall progress: Pending
// (Not Started + In Process) and Done.
const FILTERS: { value: WishPoolFilter; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "done", label: "Done" },
];
const STATUSES = ["open", "in_progress", "done"] as const;

const EngagementList = ({
  appKey,
  scope,
  status,
  setView,
  empty,
  breadcrumb,
}: {
  appKey: string;
  scope: WishPoolScope;
  status?: WishPoolFilter;
  setView: (view: { scope?: string; status?: string }) => void;
  empty?: string;
  breadcrumb?: { label?: string; to?: string }[];
}) => {
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  // keepPreviousData: switching views keeps the tabs and old rows on screen
  // until the new page arrives, instead of flashing a full-page loader.
  const { data, isLoading } = useSWR<WishPoolResult>(
    swrKey("/engagements/wish-pool", { appId: appKey, scope, status, pageIndex, pageSize }),
    { keepPreviousData: true },
  );
  const changeView = (view: { scope?: string; status?: string }) => {
    setPageIndex(0);
    setView(view);
  };
  const rows = data?.data ?? [];
  const total = data?.total;
  useEffect(() => {
    if (total === undefined || isLoading) return;
    const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (pageIndex > lastPage) setPageIndex(lastPage);
  }, [total, isLoading, pageIndex, pageSize]);
  const allTags = useTags();
  // Rows are filtered and paginated in SQL. Counts are aggregates, never
  // derived from a partial page or by downloading completed history.
  const allCounts = data?.counts.all;
  const appTotal = STATUSES.reduce((sum, s) => sum + (data?.counts.all[s] ?? 0), 0);

  if (!data) return <ListLoading />;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={scope} onValueChange={(value) => changeView({ scope: value })}>
          <TabsList aria-label="范围">
            {SCOPES.map((v) => (
              <TabsTrigger key={v.value} value={v.value}>{v.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {scope === "all" && (
          <Tabs value={status} onValueChange={(value) => changeView({ status: value })}>
            <TabsList
              aria-label="整体进度"
              className="max-w-full overflow-x-auto"
            >
              {/* Done carries no number: it only grows and isn't actionable. */}
              {FILTERS.map((f) => (
                <TabsTrigger key={f.value} value={f.value}>
                  {f.label}
                  {f.value === "pending" && ` (${(allCounts?.open ?? 0) + (allCounts?.in_progress ?? 0)})`}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
      </div>
      {rows.length === 0 && <ListEmpty>{appTotal > 0 ? "No issues here." : empty || "None."}</ListEmpty>}
      <div
        aria-busy={isLoading}
        className={cn("flex flex-col gap-1", isLoading && "opacity-60")}
      >
        {rows.map((e) => {
          const prd = e.prd as Prd;
          const myStatus = personalStatusOf(e);
          const version = engagementVersion(e);
          const inPool = !version;
          const tags = (prd.tagIds ?? [])
            .map((id) => allTags.find((t) => t.id === id))
            .filter((t): t is (typeof allTags)[number] => !!t);
          return (
            <RowCard
              key={e.id}
              onClick={() =>
                navigate(engagementDetailPath(e.id), {
                  state: { breadcrumb },
                })
              }
            >
              {/* Leading circle: my own status in Me (In Process spins,
                  shown at every width — it's the only status marker on the
                  row); everyone's overall progress in All (static, hidden
                  on phones to save room). */}
              {scope === "me" ? (
                <MyStatusIcon status={myStatus} />
              ) : (
                <div className="hidden md:contents">
                  <CompletionProgress engagement={e} />
                </div>
              )}
              <CopyShortId shortId={prd.shortId} />
              {/* Priority is read-only here (owner 2026-09-12); scheduled rows
                  adjust it on the version page. */}
              <PriorityChip engagement={e} />
              <IssueTypeChip prd={prd} />
              {/* PRs follow the title, as on Inbox rows. */}
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate">{prd.docName}</span>
                <PrLinks prd={prd} variant="text" />
              </div>
              {/* Labels before the version chip: tags vary in count/length,
                  the version chip is a fixed anchor at the row's right side. */}
              {/* Hidden on phones: labels are secondary — the title keeps
                  its room */}
              {tags.map((t) => (
                <TagPill
                  key={t.id}
                  tag={t.name}
                  color={t.color}
                  to={`/tags/${t.id}`}
                  className="hidden shrink-0 lg:inline-flex"
                />
              ))}
              {/* The "ride" slot: scheduled rows wear the version chip, pool
                  rows offer 请求上车 in the same position (mutually
                  exclusive). */}
              {version && (
                <Chip tone="green" className="hidden md:inline">
                  {version.versionName ?? version.id}
                </Chip>
              )}
              {prd.todoCounts && (
                <span
                  className={cn(
                    "hidden w-16 shrink-0 items-center justify-end gap-1 text-xs tabular-nums md:inline-flex",
                    prd.todoCounts.completed === prd.todoCounts.total
                      ? "text-foreground"
                      : "text-destructive"
                  )}
                  title={`Todo：已完成 ${prd.todoCounts.completed} 项，共 ${prd.todoCounts.total} 项`}
                  aria-label={`Todo：已完成 ${prd.todoCounts.completed} 项，共 ${prd.todoCounts.total} 项`}
                >
                  <ListTodoIcon className="size-3 shrink-0" aria-hidden="true" />
                  {prd.todoCounts.completed}/{prd.todoCounts.total}
                </span>
              )}
              {/* Phones keep the title, my status circle and owner (version and todo progress hide
                  below md to save room); the schedule /
                  back-to-pool actions hide below md — both are
                  also on the PRD detail page. `md:contents` keeps the
                  wrapper out of the flex layout (no extra gap). */}
              {inPool && (
                <div className="hidden md:contents">
                  <ScheduleVersionDialog prd={prd} pid={appKey} />
                </div>
              )}
              {/* Rows on a 规划中 version can hop back off. */}
              {!inPool && version?.state === STATE_PLANNING && (
                <div className="hidden md:contents">
                  <BackToPoolButton
                    engagementId={e.id}
                    onDone={() => mutateRequest("/engagements")}
                  />
                </div>
              )}
              {/* Exec stage: fixed slot right before the owner. */}
              <ExecStageChip prd={prd} fixed />
              <OwnerCell prd={prd} />
              {prd.createdAt && (
                <span className="hidden shrink-0 text-xs tabular-nums text-muted-foreground md:block">
                  {format(new Date(prd.createdAt), DATE_SHORT)}
                </span>
              )}
              {prd.type !== "doc" && (
                <div className="hidden md:contents">
                  <ExecMenu
                    prdId={e.id}
                    appId={appKey}
                    shortId={e.shortId ?? undefined}
                    engagement={e}
                    compact
                  />
                </div>
              )}
            </RowCard>
          );
        })}
      </div>
      <DataTablePagination total={data.total} store={{
        pageIndex, pageSize, setPageIndex,
        setPageSize: (size) => { setPageSize(size); setPageIndex(0); },
      }} />
    </div>
  );
};

// Prds tab content. The app header (name / group / create actions) lives
// in the layout above the tabs, so it persists across the app's tabs.
const AppDetail = () => {
  const { id } = useParams();
  const app = useApp(id);
  // Me always shows one pending list (WED-237); status only filters All.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = SCOPES.find((v) => v.value === searchParams.get("scope"))?.value ?? "me";
  // Legacy open / in_progress links land on Pending.
  const status: WishPoolFilter | undefined = scope === "all"
    ? searchParams.get("status") === "done" ? "done" : "pending"
    : undefined;
  // One URL update for scope + status; defaults stay out of the URL.
  const setView = (view: { scope?: string; status?: string }) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("mine");
        if (view.scope !== undefined) next.delete("status");
        for (const [key, value, fallback] of [
          ["scope", view.scope, "me"],
          ["status", view.status, "pending"],
        ] as const) {
          if (value === undefined) continue;
          if (value === fallback) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );

  if (!app) {
    return (
      <div className="p-4 text-sm text-muted-foreground">App not found.</div>
    );
  }

  const appKey = String(app.code ?? app.id);

  // PRDs and bugs mix by default; bug rows carry a red chip.
  return (
    <EngagementList
      key={appKey}
      appKey={appKey}
      scope={scope}
      status={status}
      setView={setView}
      empty="No issues related to this app."
      breadcrumb={[{ label: app.name, to: `/apps/${id}/wish-pool` }]}
    />
  );
};

export default AppDetail;
